import { Box } from "@mui/material";
import React, { useState } from "react";
import ImageBox from "../../../shared/components/ImageBox";
import Location from "../../../shared/components/Location";
import CalendarIcon from "../../../shared/assets/icons/CalendarIcon";
import PersonsIcon from "../../../shared/assets/icons/PersonsIcon";
import { customerSize, customerTurnover } from "../../../shared/models/models";
import CoinSwapIcon from "../../../shared/assets/icons/CoinSwapIcon";
import TelephoneReceiverIcon from "../../../shared/assets/icons/TelephoneReceiverIcon";
import colors from "../../../shared/themes/colors";
import LetterIcon from "../../../shared/assets/icons/LetterIcon";
import WebsiteIcon from "../../../shared/assets/icons/WebsiteIcon";
import { NavLink } from "react-router-dom";
import FacebookLogoIcon from "../../../shared/assets/icons/FacebookLogoIcon";
import LinkedinLogoIcon from "../../../shared/assets/icons/LinkedinLogoIcon";
import InstagramLogoIcon from "../../../shared/assets/icons/InstagramLogoIcon";
import XLogoIcon from "../../../shared/assets/icons/XLogoIcon";
import Tooltip from "../../../shared/components/Tooltip";
import { CustomerCompanyDetails } from "../../companies/models/models";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../../../shared/themes/redesignTokens";

interface CompanyDetailsProps {
  company: CustomerCompanyDetails;
  isCompanySearch?: boolean;
}

const CompanyDetails = ({ company, isCompanySearch = false }: CompanyDetailsProps) => {
  const [copyField, setCopyField] = useState<string>();
  const { translate } = useTranslation("marketplace");
  const { translate: tCommon } = useTranslation("common");

  const onCopy = (value: string | undefined, copyField: string) => {
    if (value && navigator.clipboard) {
      navigator.clipboard.writeText(value);
      setCopyField(copyField);
    }
  };

  const contactHover = {
    ":hover": {
      color: dt.blue
    }
  };

  return (
    <Box className={isCompanySearch ? "" : "flex absolute ml-4 pb-3"} sx={{ top: "92px" }}>
      <Box
        sx={{
          width: isCompanySearch ? "auto" : "320px",
          background: isCompanySearch ? "" : "#FAFAFA",
          zIndex: "100",
          padding: isCompanySearch ? 0 : "16px",
          borderRadius: "8px"
        }}
      >
        <Box className={isCompanySearch ? "" : "flex justify-center relative"}>
          {company?.logo?.content && <ImageBox src={company?.logo?.content} />}
        </Box>

        <Box>
          <Box
            className="text-20-medium"
            sx={{ marginTop: company?.logo?.content ? "mt-4" : "mt-0", maxWidth: isCompanySearch ? "auto" : "260px" }}
          >
            {company?.name}
          </Box>

          <Box className="mt-4">
            <Location
              countryCode={company?.address.countryId}
              city={company?.address.city}
              textClassName="text-16-regular"
              flagSize="medium"
            />
          </Box>

          <Box className="gap-4">
            {company?.sizeCode && (
              <Box className="flex mt-2">
                <PersonsIcon color="#C7C9CD" />
                <Box className="text-16-regular ml-2 flex items-center" color={"#333333"}>
                  {customerSize(company.sizeCode)}
                </Box>
              </Box>
            )}

            {company?.turnoverCode && (
              <Box className="flex mt-2">
                <CoinSwapIcon color="#C7C9CD" />
                <Box className="text-16-regular ml-2 flex items-center" color={"#333333"}>
                  {customerTurnover(company.turnoverCode)}
                </Box>
              </Box>
            )}

            {company?.establishingYear && (
              <Box className="flex mt-2">
                <CalendarIcon color="#C7C9CD" />
                <Box className="text-16-regular ml-2" color={"#333333"}>
                  {company.establishingYear}
                </Box>
              </Box>
            )}
          </Box>

          <Box className="mt-6">
            <Box className="text-16-bold" color={"#333333"}>
              {tCommon("companyRegistrationDetails")}
            </Box>

            <Box className="mt-2">
              <Box className="text-16-regular flex break-word">
                <Box className="whitespace-nowrap">{tCommon("crn")}</Box>
                <span style={{ color: "#333333", marginLeft: "8px" }}>{company?.registrationNumber || "—"}</span>
              </Box>
              <Box className="text-16-regular flex break-word">
                <Box className="whitespace-nowrap">{tCommon("vat")}</Box>
                <span style={{ color: "#333333", marginLeft: "13px" }}>{company?.vatId || "—"}</span>
              </Box>
              <Box className="text-16-regular flex break-word">
                <Box className="whitespace-nowrap">{tCommon("tax")}</Box>
                <span style={{ color: "#333333", marginLeft: "16px" }}>{company?.taxId || "—"}</span>
              </Box>
            </Box>
          </Box>

          {(company?.phone || company?.email || company?.website) && (
            <Box className="mt-6">
              <Box className="text-16-bold" color={"#333333"}>
                {tCommon("contact")}
              </Box>

              <Box className="text-16-regular" color={"#333333"}>
                <Box className="flex gap-2 mt-2 w-full">
                  <TelephoneReceiverIcon />
                  <Tooltip
                    title={
                      copyField === "phone" ? (
                        <Box className="text-12-bold" color={colors.primary.white1}>
                          {tCommon("copied")}
                        </Box>
                      ) : (
                        <Box className="text-12-bold" color={colors.primary.white1}>
                          {tCommon("copy")}
                        </Box>
                      )
                    }
                    placement="top"
                    arrow
                  >
                    <Box
                      sx={contactHover}
                      className="cursor-pointer"
                      onClick={() => onCopy(company.phone, "phone")}
                    >
                      {company.phone}
                    </Box>
                  </Tooltip>
                </Box>
              </Box>

              <Box className="text-16-regular" color={"#333333"}>
                <Box className="flex gap-2 mt-2 w-full">
                  <LetterIcon />
                  <Tooltip
                    title={
                      copyField === "email" ? (
                        <Box className="text-12-bold" color={colors.primary.white1}>
                          Copied!
                        </Box>
                      ) : (
                        <Box className="text-12-bold" color={colors.primary.white1}>
                          Copy
                        </Box>
                      )
                    }
                    placement="top"
                    arrow
                  >
                    <Box
                      sx={contactHover}
                      className="cursor-pointer break-all"
                      onClick={() => onCopy(company.email, "email")}
                    >
                      {company.email}
                    </Box>
                  </Tooltip>
                </Box>
              </Box>

              <Box className="text-16-regular" color={"#333333"}>
                <Box className="flex gap-2 mt-2 w-full">
                  <WebsiteIcon />
                  <NavLink className="break-all" to={company?.website} target="_blank">
                    <Box sx={contactHover}>{company?.website}</Box>
                  </NavLink>
                </Box>
              </Box>
            </Box>
          )}

          <Box className="flex items-center gap-4">
            {company?.facebookLink && (
              <NavLink className="mt-6" to={"https://facebook.com/" + company.facebookLink} target="_blank">
                <FacebookLogoIcon />
              </NavLink>
            )}

            {company?.linkedInLink && (
              <NavLink className="mt-6" to={"https://linkedin.com/" + company.linkedInLink} target="_blank">
                <LinkedinLogoIcon />
              </NavLink>
            )}

            {company?.instagramLink && (
              <NavLink className="mt-6" to={"https://instagram.com/" + company.instagramLink} target="_blank">
                <InstagramLogoIcon />
              </NavLink>
            )}

            {company?.xLink && (
              <NavLink className="mt-6" to={"https://x.com/" + company.xLink} target="_blank">
                <XLogoIcon />
              </NavLink>
            )}
          </Box>

          {company?.address.addressLine && (
            <Box className="mt-6">
              <Box className="text-16-bold" color={"#333333"}>
                {translate("mainOffice")}
              </Box>

              <Box className="text-16-regular mt-2" color={"#333333"}>
                {company?.address.addressLine}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
export default CompanyDetails;